import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import ImageUpload from '../../components/ImageUpload';

function CategoriesList() {
  const [categories, setCategories] = useState(null);
  const [editing, setEditing] = useState(null); // null | {} | {id, ...}
  const [error, setError] = useState(null);

  const load = () => {
    setError(null);
    axios.get('/api/admin/categories')
      .then(res => setCategories(res.data))
      .catch(err => {
        const status = err.response?.status;
        const body = err.response?.data;
        setError(
          `טעינה נכשלה (${status || 'no response'}): ${typeof body === 'object' ? JSON.stringify(body) : body || err.message}`
        );
        setCategories([]);
      });
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (c) => {
    if (!window.confirm(`למחוק את הקטגוריה "${c.name}"?`)) return;
    try {
      await axios.delete(`/api/admin/categories/${c.id}`);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'מחיקה נכשלה');
    }
  };

  if (categories === null && !error) return <div className="loading">טוען...</div>;
  if (error) {
    return (
      <div style={{ padding: '1rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#b91c1c' }}>
        {error}
      </div>
    );
  }

  return (
    <>
      <div className="breadcrumb">
        <Link to="/products">מוצרים</Link>
        <span>/</span>
        <span>קטגוריות</span>
      </div>

      <div className="section-header">
        <h3>קטגוריות מוצרים</h3>
        <button className="btn btn-primary btn-sm" onClick={() => setEditing({})}>+ קטגוריה חדשה</button>
      </div>

      <p style={{ color: '#6b7280', fontSize: '0.85rem', marginBottom: '1rem' }}>
        לכל קטגוריה שני אייקונים - אחד למצב סגור ואחד למצב פתוח. הם יופיעו על המפה ליד כל שער ששייך אליה.
      </p>

      <div className="category-grid">
        {categories.map(c => (
          <div key={c.id} className="category-card">
            <div className="category-card-header">
              <h4>{c.name}</h4>
              <span className="badge-light">{c.products_count} מוצרים</span>
            </div>
            <div className="category-icons">
              <div className="category-icon-slot">
                <div className="category-icon-label">סגור</div>
                {c.icon_closed_url ? (
                  <img src={c.icon_closed_url} alt="closed" />
                ) : (
                  <div className="category-icon-empty">-</div>
                )}
              </div>
              <div className="category-icon-slot">
                <div className="category-icon-label">פתוח</div>
                {c.icon_open_url ? (
                  <img src={c.icon_open_url} alt="open" />
                ) : (
                  <div className="category-icon-empty">-</div>
                )}
              </div>
            </div>
            <div className="category-card-actions">
              <button className="btn btn-secondary btn-sm" onClick={() => setEditing(c)}>ערוך</button>
              <button className="btn btn-danger btn-sm" onClick={() => handleDelete(c)}>מחק</button>
            </div>
          </div>
        ))}
        {categories.length === 0 && (
          <p style={{ color: '#9ca3af' }}>אין קטגוריות עדיין. צור את הראשונה.</p>
        )}
      </div>

      {editing !== null && (
        <CategoryModal
          category={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </>
  );
}

function CategoryModal({ category, onClose, onSaved }) {
  const isNew = !category.id;
  const [form, setForm] = useState({
    name: category.name || '',
    icon_closed_url: category.icon_closed_url || null,
    icon_open_url: category.icon_open_url || null,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      if (isNew) {
        await axios.post('/api/admin/categories', form);
      } else {
        await axios.put(`/api/admin/categories/${category.id}`, form);
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'שמירה נכשלה');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <form className="modal" onClick={e => e.stopPropagation()} onSubmit={handleSubmit}>
        <h2>{isNew ? 'קטגוריה חדשה' : 'עריכת קטגוריה'}</h2>

        <div className="form-group">
          <label>שם הקטגוריה</label>
          <input
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            placeholder="למשל: שער מגזרי"
            required
          />
        </div>

        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <div style={{ flex: 1 }}>
            <ImageUpload
              type="icons"
              label="אייקון מצב סגור"
              value={form.icon_closed_url}
              onChange={url => setForm({ ...form, icon_closed_url: url })}
            />
          </div>
          <div style={{ flex: 1 }}>
            <ImageUpload
              type="icons"
              label="אייקון מצב פתוח"
              value={form.icon_open_url}
              onChange={url => setForm({ ...form, icon_open_url: url })}
            />
          </div>
        </div>

        {error && <div style={{ color: '#dc2626', marginTop: '0.75rem' }}>{error}</div>}

        <div className="modal-actions">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'שומר...' : 'שמור'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={onClose}>ביטול</button>
        </div>
      </form>
    </div>
  );
}

export default CategoriesList;
